import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "What comes after medication and therapy for depression?",
    answer:
      "After adequate medication and therapy without satisfactory improvement, options include FDA-cleared TMS, SAINT® accelerated TMS, FDA-approved SPRAVATO® (esketamine), medication augmentation, and ECT for severe cases. A psychiatric evaluation reviews your treatment history and recommends the next appropriate step based on your individual circumstances.",
  },
  {
    question: "Does trying TMS mean I have to stop therapy?",
    answer:
      "No. TMS and psychotherapy are often used together. Many patients continue therapy during and after TMS. Combining approaches is often more effective than any single treatment. Do not stop therapy or medication without consulting your providers.",
  },
  {
    question: "What if medication and therapy haven't worked at all?",
    answer:
      "Persistent symptoms after adequate medication and therapy may indicate treatment-resistant depression. A psychiatrist can confirm whether your trials were truly adequate, screen for conditions that mimic resistance, and recommend TMS, SAINT®, SPRAVATO®, or other options. If you are having thoughts of self-harm, call or text 988 now.",
  },
];

export const pageWhatComesAfterMedTherapy: LearnPageData = {
  slug: "what-comes-after-medication-and-therapy-for-depression",
  title: "What Comes After Medication and Therapy? | Neuro Wellness",
  description:
    "When medication and therapy haven't worked, options include TMS, SAINT®, SPRAVATO®, and ECT. Learn what a psychiatrist may recommend next.",
  canonical: "/learn/what-comes-after-medication-and-therapy-for-depression",
  h1: "What Comes After Medication and Therapy for Depression?",
  breadcrumbLabel: "After Medication and Therapy",
  crumbs: baseCrumbs("After Medication and Therapy"),
  shortAnswer:
    "After adequate medication and therapy without satisfactory improvement, options include FDA-cleared TMS, SAINT® accelerated TMS, FDA-approved SPRAVATO® (esketamine), medication augmentation, and ECT for severe cases. A psychiatric evaluation reviews your treatment history and recommends the next appropriate step. If you are having thoughts of self-harm, call or text 988 (Suicide & Crisis Lifeline) now.",
  keyTakeaways: [
    "TMS and SAINT® are FDA-cleared for adults whose depression hasn't improved on antidepressants.",
    "SPRAVATO® is FDA-approved for treatment-resistant depression.",
    "ECT has the highest remission rates for severe or psychotic depression.",
    "Combining treatments is often more effective than any single approach.",
    "If you are in crisis, call or text 988 now.",
  ],

  sections: [
    {
      heading: "Confirming Your Treatment Was Adequate",
      body: (
        <>
          {P(
            "Before moving to the next step, a psychiatrist confirms that your medication trials were adequate (therapeutic dose, 6 to 8 weeks) and that therapy was delivered at sufficient intensity. They also screen for bipolar disorder, medical conditions, and substance use that can mimic treatment resistance.",
          )}
        </>
      ),
    },
    {
      heading: "Non-Medication Options",
      body: (
        <>
          {P(
            "FDA-cleared TMS delivers magnetic pulses to the prefrontal cortex over about 6 weeks of daily sessions. SAINT® is an accelerated, fMRI-guided form of TMS delivered in 5 consecutive days. Both are non-invasive, medication-free, and do not require anesthesia. SPRAVATO® (esketamine) is an FDA-approved nasal spray for treatment-resistant depression, administered in a certified clinic with 2 hours of monitoring.",
          )}
        </>
      ),
    },
    {
      heading: "When to Consider ECT",
      body: (
        <>
          {P(
            "Electroconvulsive therapy (ECT) has the highest remission rates for severe or psychotic depression but requires anesthesia and carries cognitive risks. It is generally reserved for severe cases that have not responded to other treatments, or when rapid response is medically necessary. A psychiatrist can discuss whether ECT is appropriate.",
          )}
        </>
      ),
    },
    {
      heading: "Questions to Ask Your Psychiatrist",
      body: (
        <>
          {P(
            "Were my medication and therapy trials truly adequate? Am I a candidate for TMS or SAINT®? What does my insurance cover? How quickly can I start? What is the plan if the first treatment doesn't work?",
          )}
          {P(
            <>
              <a
                href="/depression-treatment-not-working"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                See our full guide on when treatment isn't working
              </a>
              .
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "APA Practice Guideline for Treatment of Patients with Major Depressive Disorder.",
      url: "https://psychiatryonline.org/",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/what-comes-after-medication-and-therapy-for-depression#webpage`,
        name: "What Comes After Medication and Therapy?",
        url: `${SITE_URL}/learn/what-comes-after-medication-and-therapy-for-depression`,
        description: "Options after medication and therapy for depression.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/what-comes-after-medication-and-therapy-for-depression#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("After Medication and Therapy")),
    ],
  },
  relatedLinks: [
    {
      title: "When Depression Treatment Isn't Working",
      href: "/depression-treatment-not-working",
      description: "Full hub with all options",
    },
    {
      title: "SAINT® TMS",
      href: "/saint",
      description: "5-day accelerated TMS",
    },
    {
      title: "SPRAVATO®",
      href: "/services/spravato",
      description: "FDA-approved esketamine",
    },
    {
      title: "Am I a Candidate?",
      href: "/am-i-a-candidate",
      description: "Request a consultation",
    },
  ],
};
